/**
 * Thin wrapper around Razorpay Checkout (checkout.js). Loads the script on demand
 * and opens the hosted modal, resolving with the gateway's payment response so the
 * caller can POST it to /pay/verify.
 */
import type { Checkout } from './types';

type RazorpaySuccess = {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
};

type RazorpayOptions = {
  key: string;
  order_id: string;
  amount: number;
  currency: string;
  name: string;
  description?: string;
  prefill?: { contact?: string };
  handler: (res: RazorpaySuccess) => void;
  modal?: { ondismiss?: () => void };
};

type RazorpayFailure = {
  error?: {
    description?: string;
    reason?: string;
    step?: string;
    source?: string;
    code?: string;
  };
};

type RazorpayInstance = {
  open: () => void;
  /** Checkout emits `payment.failed` when the gateway declines. Without subscribing, a
   *  declined payment is indistinguishable from the customer closing the sheet. */
  on: (event: 'payment.failed', handler: (res: RazorpayFailure) => void) => void;
};

declare global {
  interface Window {
    Razorpay?: new (opts: RazorpayOptions) => RazorpayInstance;
  }
}

const SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
let script_promise: Promise<void> | null = null;

function load_script(): Promise<void> {
  if (window.Razorpay) return Promise.resolve();
  if (script_promise) return script_promise;
  script_promise = new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = SCRIPT_SRC;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => {
      script_promise = null;
      reject(new Error('Failed to load the payment gateway. Check your connection and retry.'));
    };
    document.body.appendChild(el);
  });
  return script_promise;
}

/** Open Checkout for a booking. Resolves with the success payload, or rejects if
 *  the gateway fails to load. Dismissing the modal calls on_dismiss and never resolves.
 *
 *  `on_failure` fires when the gateway *declines* the payment. Subscribing matters: without
 *  it a decline looked exactly like an abandoned checkout — the sheet closed, the screen was
 *  unchanged, and nothing was recorded anywhere (BUG-040). */
export async function open_razorpay_checkout(opts: {
  checkout: Checkout;
  service_name: string;
  contact?: string;
  on_success: (res: RazorpaySuccess) => void;
  on_dismiss?: () => void;
  on_failure?: (message: string) => void;
}): Promise<void> {
  await load_script();
  if (!window.Razorpay) throw new Error('Payment gateway unavailable.');
  if (!opts.checkout.key_id) throw new Error('Payment is not configured. Please contact support.');

  const rzp = new window.Razorpay({
    key: opts.checkout.key_id,
    order_id: opts.checkout.order_ref,
    amount: opts.checkout.amount_paise,
    currency: opts.checkout.currency,
    name: 'Pet Marketplace',
    description: opts.service_name,
    prefill: opts.contact ? { contact: opts.contact } : undefined,
    handler: opts.on_success,
    modal: opts.on_dismiss ? { ondismiss: opts.on_dismiss } : undefined,
  });

  if (opts.on_failure) {
    rzp.on('payment.failed', (res) => {
      const e = res?.error;
      // Razorpay's `description` is the customer-facing sentence; fall back through the
      // machine fields so we never surface an empty error.
      const detail = e?.description ?? e?.reason ?? e?.code ?? 'the payment could not be completed';
      opts.on_failure?.(`Payment failed — ${detail}. You have not been charged. Please try again.`);
    });
  }

  rzp.open();
}
